import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import useStoryExpiry from '../../hooks/useStoryExpiry';
import { setStories } from '../../slice/storiesSlice';
import SDK from '../SDK';
import logger from '../../Helpers/logger';
import StoriesCard from './StoriesCard';

const PAGE_SIZE = 20;

const StoriesList = ({ handleShowStoryPreview }) => {
    useStoryExpiry();

    const dispatch = useDispatch();

    const allStories = useSelector(state => state.stories.stories);

    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false);

    const recentStories = useMemo(() => allStories.filter(story => story.hasUnseen), [allStories]);

    const viewedStories = useMemo(() => allStories.filter(story => !story.hasUnseen), [allStories]);

    const fetchStoriesFeed = async (currentPage = 0) => {
        if (isFetching) return;
        setIsFetching(true);

        try {
            const response = await SDK.fetchStoriesFeed({
                page: currentPage,
                limit: PAGE_SIZE,
            });

            const { data = [], totalRecords = 0 } = response.data;

            const updatedStories = currentPage === 0 ? data : [...(allStories || []), ...data];

            dispatch(setStories(updatedStories));

            const loadedCount = currentPage * PAGE_SIZE + data.length;

            setHasMore(loadedCount < totalRecords);
        } catch (err) {
            logger.error('Failed to fetch stories:', err);
        } finally {
            setIsFetching(false);
        }
    };

    const loadMore = () => {
        setPage(prev => {
            const next = prev + 1;
            fetchStoriesFeed(next);
            return next;
        });
    };

    useEffect(() => {
        fetchStoriesFeed(0);
    }, []);

    const noStories = recentStories.length === 0 && viewedStories.length === 0;

    if (noStories) {
        return (
            <div className="no-stories">
                <h4>{'No Stories set'}</h4>
            </div>
        );
    }

    return (
        <InfiniteScroll
            dataLength={allStories.length}
            next={loadMore}
            hasMore={hasMore}
            scrollableTarget="scrollableUl-stories"
        >
            {recentStories.length > 0 && (
                <div className="stories-list">
                    <StoriesCard
                        title="Recent updates"
                        items={recentStories}
                        isViewed={false}
                        onShowPreview={handleShowStoryPreview}
                    />
                </div>
            )}

            {viewedStories.length > 0 && (
                <div className="stories-list">
                    <StoriesCard
                        title="Viewed"
                        items={viewedStories}
                        isViewed={true}
                        onShowPreview={handleShowStoryPreview}
                    />
                </div>
            )}
        </InfiniteScroll>
    );
};

export default StoriesList;
